// Saves a presigned file link to the user's downloads. Sibling to
// uploadToPresignedUrl and bypasses the shared axios client for the same reason:
// its interceptors would JSON-stringify the request and try to decrypt the
// storage provider's response.
//
// The bytes are fetched rather than handed to an <a href> because the link is
// cross-origin, where the `download` attribute is IGNORED and the bucket's
// Content-Disposition decides between saving and previewing. Buffering the
// response into a blob takes that decision back: it always saves, always under
// the name we pass, and never opens a tab over the page being read.
//
// Plain `fetch` with no Authorization header — the signature IS the
// authorisation, and an extra header can invalidate it.

/** Save text the app generated locally (no network) as `filename`. Same anchor
 *  dance as below, minus the fetch — there is nothing to retrieve, the bytes are
 *  already in hand. Used for the data source CSV template. */
export const downloadTextFile = (
  content: string,
  filename: string,
  mimeType = 'text/csv;charset=utf-8',
): void => {
  const href = URL.createObjectURL(new Blob([content], { type: mimeType }));
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 0);
};

/** Fetch a file URL and save it as `filename`. Throws on a non-2xx. */
export const downloadFileUrl = async (url: string, filename: string): Promise<void> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Download failed (${res.status})`);

  const href = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked on the next task, not inline: the click's navigation must still be
  // able to resolve the object URL.
  setTimeout(() => URL.revokeObjectURL(href), 0);
};
